import { useEffect, useRef, useState, useMemo } from 'react';
import { offlineStore } from '../lib/OfflineStore';
import { downloadManager } from '../lib/DownloadManager';
import { loadSettings } from '../settings/voiceSettings';
import { LibraryDB } from '../services/LibraryDB';
import { getCoverUrl } from '../utils/coverHelper';

interface AudioPlayerOverlayProps {
    url: string;
    title: string;
    coverUrl?: string | null;
    startTime?: number; // P0-D: Trim support
    bookId?: string;
    onClose: () => void;
    isMember?: boolean; // P0-C: Book Club Membership
    upsellUrl?: string | null; // P0-D: Optional Upsell Clip
}

interface PlaylistTrack {
    key?: string;
    url?: string;
    index: number;
    size?: number;
}

interface PlaylistResponse {
    ok?: boolean;
    tracks: PlaylistTrack[];
    error?: string;
}

export const AudioPlayerOverlay = ({ url, title, coverUrl, startTime, bookId, onClose, isMember = false, upsellUrl }: AudioPlayerOverlayProps) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    // Helper: Extract filename from track.key (which might be full R2 path)
    const getFilename = (track: PlaylistTrack): string => {
        // track.key might be full R2 path like "library/bookId/audio/0.mp3"
        // We need just the filename like "0.mp3" for OPFS storage
        if (track.key && /\.(mp3|m4a|wav|ogg)$/i.test(track.key)) {
            return track.key.split('/').pop() || 'audio.mp3';
        }
        // Fallback: extract from URL
        const urlPath = track.url ? track.url.split('?')[0] : '';
        return urlPath.split('/').pop() || 'audio.mp3';
    };

    // Playlist State
    const [tracks, setTracks] = useState<PlaylistTrack[]>([]);
    const [currentIndex, setCurrentIndex] = useState(-1);
    const [seekApplied, setSeekApplied] = useState(false);

    // Track play gesture state
    const pendingPlayRequestedRef = useRef(false);

    // Centralized playback function
    const playTrack = (index: number, autoplay = true) => {
        console.log(`[AudioPlayerOverlay] playTrack index=${index} autoplay=${autoplay}`);
        pendingPlayRequestedRef.current = autoplay;
        if (index === currentIndex) {
            // Index is same. If we want to play, try immediately.
            if (autoplay && audioRef.current && audioRef.current.src) {
                console.log("[AudioPlayerOverlay] playTrack index unchanged. Playing immediately.");
                audioRef.current.play().catch(e => {
                    console.error("[AudioPlayerOverlay] playTrack immediate play failed:", e);
                    setError(`Playback failed: ${e.message || e}. Tap Play.`);
                });
            }
        } else {
            setCurrentIndex(index);
        }
    };

    // Preview / Upsell State
    const [showUpsell, setShowUpsell] = useState(false);
    // Track total elapsed time across chunks? Or just cutoff at 5 mins of play?
    // Requirement: "Track elapsed PLAY time". Simplest: session timer or timeUpdate on track 0?
    // "At 5:00 elapsed". If precise 5 mins total, we need a cumulative timer. 
    // BUT simplest UX: Cutoff if currentTime > 300s (5 mins) on Track 1? 
    // Or if they play Track 2? 
    // Let's implement: If (elapsedTotal > 300) -> Cutoff.
    // For MVP robustness: If !isMember and (cumulativeTime > 300), Stop.
    const [sessionTime, setSessionTime] = useState(0);
    const lastTickRef = useRef(Date.now());

    // Upsell Audio
    const upsellRef = useRef<HTMLAudioElement | null>(null);
    const [upsellPlaying, setUpsellPlaying] = useState(false);

    // Art Mode State
    const [artMode, setArtMode] = useState(false);
    const [hiResUrl, setHiResUrl] = useState<string | null>(null);
    const [hiResLoaded, setHiResLoaded] = useState(false);

    // Resume State
    const [resumeCandidate, setResumeCandidate] = useState<{ trackIndex: number; position: number } | null>(null);
    const [showResumePrompt, setShowResumePrompt] = useState(false);

    // Sleep Timer State
    const [sleepTimerEndAt, setSleepTimerEndAt] = useState<number | null>(() => {
        const stored = localStorage.getItem('audio_sleep_timer_endAt');
        if (stored) {
            const parsed = parseInt(stored, 10);
            if (!isNaN(parsed) && parsed > Date.now()) {
                return parsed;
            }
        }
        return null;
    });
    const [showSleepPicker, setShowSleepPicker] = useState(false);
    const [sleepToast, setSleepToast] = useState(false);

    // Force re-render periodically when sleep timer is active to update displayed remaining time
    const [, setTick] = useState(0);
    useEffect(() => {
        if (!sleepTimerEndAt) return;
        const interval = setInterval(() => setTick(t => t + 1), 10000); // 10s
        return () => clearInterval(interval);
    }, [sleepTimerEndAt]);

    // Resume data is loaded inside the coordinated loadAudio effect below.

    // Calc URL variants (v2 compatible) using getCoverUrl
    const fullCover = useMemo(() => {
        if (!coverUrl && !bookId) return null;
        return getCoverUrl(coverUrl, 'full', bookId);
    }, [coverUrl, bookId]);

    const hiCover = useMemo(() => {
        if (!coverUrl && !bookId) return null;
        return getCoverUrl(coverUrl, 'hi', bookId);
    }, [coverUrl, bookId]);

    // Preload Hi-Res on Mount (Low Priority)
    useEffect(() => {
        if (hiCover) {
            setHiResUrl(hiCover);
            const img = new Image();
            img.src = hiCover;
            img.onload = () => {
                setHiResLoaded(true);
                // console.log("Art Mode: Hi-Res loaded");
            };
        }
    }, [hiCover]);

    // 1. Resolve Audio Source (Direct URL vs Playlist) + Load Resume State Coordinated
    useEffect(() => {
        let active = true;
        const loadAudio = async () => {
            setLoading(true);
            setError(null);

            console.log("[AudioPlayerOverlay] User play clicked. Initiating coordinated load. url:", url);

            // Fetch resume candidate from LibraryDB first to avoid race conditions
            let resumeData: any = null;
            if (bookId) {
                try {
                    resumeData = await LibraryDB.getAudioResume(bookId);
                    console.log("[AudioPlayerOverlay] Coordinated resume data read result:", resumeData);
                } catch (e) {
                    console.warn("[AudioPlayerOverlay] Failed to load resume data:", e);
                }
            }

            if (!active) return;

            if (!url) {
                console.error("[AudioPlayerOverlay] No URL provided");
                setError("No audio URL found for this book.");
                setLoading(false);
                return;
            }

            // Case A: Legacy Direct File (Strict Audio Extension or Explicitly Not JSON)
            const isJson = url.toLowerCase().endsWith('.json') || url.toLowerCase().endsWith('.json?');
            const isExplicitPlaylist = url.includes('/playlist/');

            if ((url.match(/\.(mp3|m4a)$/i) || (!isExplicitPlaylist && !isJson))) {
                const directTracks = [{ key: 'direct', url: url, index: 0, size: 0 }];
                setTracks(directTracks);
                
                // Select start track based on resume candidate
                if (resumeData && resumeData.trackIndex === 0 && resumeData.positionSeconds > 10) {
                    setResumeCandidate({ trackIndex: 0, position: resumeData.positionSeconds });
                    setShowResumePrompt(true);
                    playTrack(0, false); // load but don't autoplay yet
                } else {
                    playTrack(0, true);
                }
                
                setLoading(false);
                return;
            }

            // Case B: Playlist JSON
            try {
                const res = await fetch(url);
                if (res.status === 404 || res.status === 409) {
                    throw new Error("Narration not ready yet");
                }
                if (!res.ok) {
                    throw new Error(`Playlist fetch failed: ${res.status}`);
                }

                const data: any = await res.json();
                if (!active) return;

                // Robust Parsing: Handle multiple shapes
                let rawTracks: any[] = [];
                if (Array.isArray(data)) {
                    rawTracks = data;
                } else if (Array.isArray(data.tracks)) {
                    rawTracks = data.tracks;
                } else if (Array.isArray(data.items)) {
                    rawTracks = data.items;
                } else if (Array.isArray(data.playlist)) {
                    rawTracks = data.playlist;
                } else {
                    throw new Error("Invalid playlist format: No tracks found");
                }

                if (rawTracks.length === 0) throw new Error("Narrated playlist is empty");

                // Helper to resolve relative URLs
                const resolveUrl = (relative: string) => {
                    try {
                        return new URL(relative, url).href;
                    } catch {
                        return relative;
                    }
                };

                // Validate & Normalize
                const validTracks: PlaylistTrack[] = rawTracks.map((t: any, i: number) => {
                    let trackUrl = '';
                    let key = '';

                    if (typeof t === 'string') {
                        trackUrl = resolveUrl(t);
                        key = t; // Use filename as key
                    } else if (typeof t === 'object') {
                        const rawUrl = t.url || t.src || t.uri;
                        if (rawUrl) trackUrl = resolveUrl(rawUrl);
                        key = t.key || t.id || trackUrl;
                    }

                    if (!trackUrl) return null;
                    if (trackUrl.toLowerCase().endsWith('.json')) return null;

                    return {
                        index: i,
                        url: trackUrl,
                        key: key,
                        size: t.size || 0
                    } as PlaylistTrack;
                }).filter((t): t is PlaylistTrack => t !== null && !!t.url);

                if (validTracks.length === 0) {
                    const sample = rawTracks[0] ? JSON.stringify(rawTracks[0]) : "none";
                    throw new Error(`Playlist Schema Error: No valid audio tracks found. (Sample: ${sample})`);
                }

                if (validTracks[0].url === url) {
                    throw new Error(`Recursion Error: Track 0 resolves to playlist URL.`);
                }

                setTracks(validTracks);
                console.log("[AudioPlayerOverlay] Playlist loaded successfully. Count:", validTracks.length);

                // Select start track based on resume candidate
                if (resumeData && resumeData.trackIndex >= 0 && resumeData.trackIndex < validTracks.length) {
                    if (resumeData.trackIndex > 0 || resumeData.positionSeconds > 10) {
                        console.log(`[AudioPlayerOverlay] Found resume candidate: track ${resumeData.trackIndex} at ${resumeData.positionSeconds}s`);
                        setResumeCandidate({ trackIndex: resumeData.trackIndex, position: resumeData.positionSeconds });
                        setShowResumePrompt(true);
                        playTrack(resumeData.trackIndex, false); // Load but don't autoplay yet
                    } else {
                        console.log("[AudioPlayerOverlay] Resume candidate is early track 0. Auto-playing.");
                        playTrack(0, true);
                    }
                } else {
                    console.log("[AudioPlayerOverlay] No valid resume candidate. Auto-playing track 0.");
                    playTrack(0, true);
                }

                if (bookId) {
                    const playlistBlob = new Blob([JSON.stringify(data)], { type: 'application/json' });
                    await offlineStore.saveChunk(bookId, 'playlist.json', playlistBlob, 'pinned');
                }
            } catch (e: any) {
                if (!active) return;
                let cachedUsed = false;
                let usedCachedPlaylist = false;
                let fallbackTracks: PlaylistTrack[] = [];
                if (bookId) {
                    try {
                        const cached = await offlineStore.getChunk(bookId, 'playlist.json');
                        if (cached) {
                            const cachedText = await cached.text();
                            const cachedData: PlaylistResponse = JSON.parse(cachedText);
                            if (cachedData.ok !== false && Array.isArray(cachedData.tracks) && cachedData.tracks.length > 0) {
                                const validTracks = cachedData.tracks.filter((t: any) => {
                                    if (!t.url) return false;
                                    return t.url.match(/\.(mp3|m4a)$/i);
                                });
                                if (validTracks.length > 0) {
                                    fallbackTracks = validTracks;
                                    setTracks(validTracks);
                                    console.log("[AudioPlayerOverlay] Loaded playlist from local cache. Count:", validTracks.length);
                                    
                                    // Process start track/resume
                                    if (resumeData && resumeData.trackIndex >= 0 && resumeData.trackIndex < validTracks.length) {
                                        if (resumeData.trackIndex > 0 || resumeData.positionSeconds > 10) {
                                            setResumeCandidate({ trackIndex: resumeData.trackIndex, position: resumeData.positionSeconds });
                                            setShowResumePrompt(true);
                                            playTrack(resumeData.trackIndex, false);
                                        } else {
                                            playTrack(0, true);
                                        }
                                    } else {
                                        playTrack(0, true);
                                    }

                                    cachedUsed = true;
                                    usedCachedPlaylist = true;
                                }
                            }
                        }
                    } catch { }
                }
                if (!cachedUsed) {
                    if (e && (e.message || '').match(/Failed to fetch|NetworkError|network/i)) {
                        setError("Audio isn’t downloaded yet. Download audio for offline use.");
                    } else {
                        setError(e.message || "Failed to load audio");
                    }
                } else if (usedCachedPlaylist && bookId && fallbackTracks.length > 0) {
                    const firstTrack = fallbackTracks[0];
                    if (firstTrack) {
                        try {
                            const filename = getFilename(firstTrack);
                            const cachedAudio = await offlineStore.getChunk(bookId, filename);
                            if (!cachedAudio) {
                                setError("Audio isn’t saved for offline use yet. Download audio to listen offline.");
                            }
                        } catch { }
                    }
                }
            } finally {
                if (active) setLoading(false);
            }
        };

        loadAudio();
        return () => {
            active = false;
        };
    }, [url]);

    // 2. Metadata / Trim Handler (Applies to First Chunk)
    const handleMetadata = () => {
        if (currentIndex === 0 && startTime && startTime > 0 && audioRef.current && !seekApplied) {
            audioRef.current.currentTime = startTime;
            setSeekApplied(true);
        }
    };

    const handleEnded = () => {
        if (currentIndex < tracks.length - 1) {
            console.log(`[AudioPlayerOverlay] Track ended. Auto-advancing to track ${currentIndex + 1}`);
            playTrack(currentIndex + 1, true);
        }
    };

    const handleError = () => {
        if (!audioRef.current) return;
        const err = audioRef.current.error;
        let msg = "Playback error";
        if (err) {
            msg = `Error ${err.code}: ${err.message}`;
        }

        // Try to get specific status code since audio onError doesn't provide it
        const currentSrc = tracks[currentIndex]?.url;
        if (currentSrc) {
            fetch(currentSrc, { method: 'HEAD' })
                .then(res => {
                    const ct = res.headers.get("content-type");
                    if (!res.ok) {
                        setError(`Audio chunk failed: ${res.status} ${res.statusText} (${ct}) (Track ${currentIndex})`);
                    } else if (ct && !ct.includes("audio")) {
                        setError(`Format Mismatch: Server returned ${ct}, expected audio/* (Track ${currentIndex})`);
                    } else {
                        setError(`${msg} (Track ${currentIndex})`);
                    }
                })
                .catch(() => {
                    setError(`${msg} (Network/CORS/Offline)`);
                });
        } else {
            setError(msg);
        }
    };

    // 3. Offline Logic Interceptor
    // We check if the current track is available offline (pinned or smart)
    const [offlineSrc, setOfflineSrc] = useState<string | null>(null);

    // Smart Cache Prefetcher
    useEffect(() => {
        if (!tracks.length || !bookId) return;
        const s = loadSettings();
        if (!s.cacheAudio) return; // Feature disabled

        // Current track
        const currentTrack = tracks[currentIndex];

        // Helper to cache a track
        const doCache = async (track: PlaylistTrack) => {
            if (!track.url) return;
            const filename = getFilename(track);
            // Check if exists
            const existing = await offlineStore.getChunk(bookId!, filename);
            if (existing) return; // Already cached

            // Check Quota before fetching?
            // Enforce Quota occasionally? `OfflineStore` handles LRU eviction on save?
            // No, we should call enforceQuota explicitly or on interval.
            // Let's call enforceQuota lazily after save.

            try {
                // console.log("Smart caching", track.key);
                const res = await fetch(track.url);
                if (res.ok) {
                    const blob = await res.blob();
                    await offlineStore.saveChunk(bookId!, filename, blob, 'smart');
                    await offlineStore.enforceQuota(s.cacheQuotaMB);
                }
            } catch (e) {
                // console.warn("Smart cache failed", e);
            }
        };

        // Cache Current
        if (currentTrack) doCache(currentTrack);

        // Prefetch Next (if any)
        const nextTrack = tracks[currentIndex + 1];
        if (nextTrack) doCache(nextTrack);
    }, [currentIndex, tracks, bookId]); // Re-run when index changes

    // Resolve Active Source (Offline vs Online)
    const [finalSrc, setFinalSrc] = useState<string | null>(null);

    useEffect(() => {
        const track = tracks[currentIndex];
        if (!track) {
            setFinalSrc(null);
            return;
        }

        let activeBlobUrl: string | null = null;
        let mounted = true;

        const resolve = async () => {
            const filename = getFilename(track);
            if (bookId) {
                const blob = await offlineStore.getChunk(bookId, filename);
                if (blob) {
                    // console.log("Playing from Offline Cache:", filename);
                    activeBlobUrl = URL.createObjectURL(blob);
                    if (mounted) setFinalSrc(activeBlobUrl);
                    return;
                }
            }
            // Fallback to network
            if (mounted) setFinalSrc(track.url || null);
        };

        resolve();

        return () => {
            mounted = false;
            if (activeBlobUrl) URL.revokeObjectURL(activeBlobUrl);
        };
    }, [currentIndex, tracks, bookId]);

    const activeSrc = finalSrc;

    // Download Status
    const [dlStatus, setDlStatus] = useState<any>(null);
    useEffect(() => {
        if (!bookId) return;
        // Initial
        downloadManager.getStatus(bookId).then(setDlStatus);

        const handler = (s: any) => {
            if (s.bookId === bookId) setDlStatus(s);
        };
        downloadManager.addListener(handler);
        return () => downloadManager.removeListener(handler);
    }, [bookId]);

    // Art Mode Logic
    const toggleArtMode = () => setArtMode(!artMode);

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.92)', zIndex: 3000,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            backdropFilter: 'blur(10px)'
        }} onClick={onClose}>

            {/* Backdrop Cover (Normal Mode) */}
            {!artMode && fullCover && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: -1,
                    backgroundImage: `url(${fullCover})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    opacity: 0.25,
                    filter: 'blur(20px)'
                }} />
            )}

            {/* ART MODE FULLSCREEN OVERLAY */}
            {artMode && (
                <div
                    style={{ position: 'fixed', inset: 0, background: 'black', zIndex: 4000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    onClick={(e) => { e.stopPropagation(); toggleArtMode(); }}
                >
                    {/* Layer 1: Start with Full (instant) */}
                    {fullCover && (
                        <img src={fullCover} style={{
                            position: 'absolute', width: '100%', height: '100%', objectFit: 'contain',
                            opacity: hiResLoaded ? 0 : 1, transition: 'opacity 0.8s ease-out'
                        }} />
                    )}

                    {/* Layer 2: Fade in Hi-Res */}
                    {hiResLoaded && hiResUrl && (
                        <img src={hiResUrl} style={{
                            position: 'absolute', width: '100%', height: '100%', objectFit: 'contain',
                            animation: 'fadeIn 0.8s'
                        }} />
                    )}

                    {/* Minimal Controls */}
                    <div style={{
                        position: 'absolute', bottom: '40px', left: 0, right: 0,
                        display: 'flex', gap: '30px', justifyContent: 'center', alignItems: 'center',
                        zIndex: 4001, padding: '20px',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)'
                    }} onClick={e => e.stopPropagation()}>
                        <button onClick={toggleArtMode} style={{
                            background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white',
                            width: '50px', height: '50px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.2rem', backdropFilter: 'blur(5px)'
                        }}>✕</button>

                        {/* Play/Pause Proxy */}
                        <button onClick={() => {
                            if (audioRef.current) {
                                if (audioRef.current.paused) audioRef.current.play();
                                else audioRef.current.pause();
                            }
                        }} style={{
                            background: 'white', border: 'none', color: 'black',
                            width: '64px', height: '64px', borderRadius: '50%', cursor: 'pointer', fontSize: '1.5rem',
                            display: 'flex', alignItems: 'center', justifyContent: 'center'
                        }}>
                            ⏯
                        </button>
                    </div>
                </div>
            )}

            {/* Normal Mode Content */}
            <div style={{
                background: 'rgba(0,0,0,0.6)', padding: '30px', borderRadius: '16px',
                width: '90%', maxWidth: '400px',
                boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
                display: 'flex', flexDirection: 'column', gap: '20px',
                textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)',
                visibility: artMode ? 'hidden' : 'visible'
            }} onClick={e => e.stopPropagation()}>

                {/* Cover Art in Player (Interactive) */}
                {fullCover && (
                    <div
                        onClick={(e) => { e.stopPropagation(); toggleArtMode(); }}
                        style={{
                            width: '150px', height: '225px', alignSelf: 'center', marginBottom: '10px',
                            boxShadow: '0 8px 16px rgba(0,0,0,0.4)', cursor: 'zoom-in', position: 'relative'
                        }}
                    >
                        <img src={fullCover} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '8px' }} />
                        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0, transition: 'opacity 0.2s', background: 'rgba(0,0,0,0.3)', borderRadius: '8px', color: 'white', fontSize: '1.5rem' }}
                            onMouseEnter={e => e.currentTarget.style.opacity = '1'}
                            onMouseLeave={e => e.currentTarget.style.opacity = '0'}
                        >
                            ⤢
                        </div>
                    </div>
                )}

                <h3 style={{ margin: 0, color: 'white' }}>{title}</h3>
                {bookId && bookId.startsWith('lv-') && (
                    <div style={{ fontSize: '11px', color: '#aaa', fontStyle: 'italic', marginTop: '2px', marginBottom: '5px' }}>
                        Audio courtesy of LibriVox volunteers.
                    </div>
                )}

                {/* Download Controls */}
                {bookId && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '5px' }}>
                        {!dlStatus || dlStatus.state === 'idle' ? (
                            <button
                                onClick={() => downloadManager.startDownload(bookId, url)}
                                disabled={!isMember}
                                style={{
                                    background: 'transparent', border: 'none',
                                    color: isMember ? 'var(--accent-color)' : 'var(--text-disabled)',
                                    cursor: isMember ? 'pointer' : 'not-allowed',
                                    fontSize: '0.85rem'
                                }}
                                title={isMember ? "Download for offline" : "Members Only"}
                            >
                                {isMember ? "↓ Make available offline" : "🔒 Members only dowload"}
                            </button>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', fontSize: '0.8rem', color: '#ccc' }}>
                                {dlStatus.state === 'downloading' && (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span>Downloading {dlStatus.progress}%</span>
                                        <button onClick={() => downloadManager.pauseDownload(bookId)} style={{ fontSize: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>⏸</button>
                                    </div>
                                )}
                                {dlStatus.state === 'paused' && (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ color: '#fa0' }}>Paused {dlStatus.progress}%</span>
                                        <button onClick={() => downloadManager.startDownload(bookId, url)} style={{ fontSize: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>▶</button>
                                        <button onClick={() => downloadManager.removeDownload(bookId)} style={{ fontSize: '1rem', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
                                    </div>
                                )}
                                {dlStatus.state === 'completed' && (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ color: '#4CAF50' }}>✓ Downloaded</span>
                                        <button onClick={() => { if (confirm("Remove download?")) downloadManager.removeDownload(bookId); }} style={{ fontSize: '0.9rem', background: 'none', border: 'none', cursor: 'pointer', opacity: 0.7 }}>✕</button>
                                    </div>
                                )}
                                {dlStatus.state === 'error' && (
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        <span style={{ color: '#f55' }}>Error: {dlStatus.error}</span>
                                        <button onClick={() => downloadManager.startDownload(bookId, url)} style={{ textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}>Retry</button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Chunk Navigation */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px', justifyContent: 'center' }}>
                    <button
                        onClick={() => playTrack(Math.max(0, currentIndex - 1), true)}
                        disabled={currentIndex <= 0}
                        style={{ background: 'transparent', border: 'none', color: 'white', opacity: currentIndex <= 0 ? 0.3 : 1, cursor: 'pointer', fontSize: '1.5rem' }}
                    >
                        ◀
                    </button>
                    <div style={{ fontSize: '0.9rem', opacity: 0.8, color: '#aaa' }}>
                        {tracks.length > 1 ? `Part ${currentIndex + 1}/${tracks.length}` : 'Narrated Edition (AI)'}
                    </div>
                    <button
                        onClick={() => playTrack(Math.min(tracks.length - 1, currentIndex + 1), true)}
                        disabled={currentIndex >= tracks.length - 1}
                        style={{ background: 'transparent', border: 'none', color: 'white', opacity: currentIndex >= tracks.length - 1 ? 0.3 : 1, cursor: 'pointer', fontSize: '1.5rem' }}
                    >
                        ▶
                    </button>
                </div>

                {loading && (
                    <div style={{ padding: '20px', fontSize: '1.2rem', opacity: 0.6, color: 'white' }}>
                        Loading audio...
                    </div>
                )}

                {error && (
                    <div style={{
                        color: '#ff6b6b',
                        fontSize: '0.85rem',
                        background: 'rgba(255,107,107,0.1)',
                        padding: '10px',
                        borderRadius: '8px',
                        wordBreak: 'break-all'
                    }}>
                        {error}
                    </div>
                )}

                {/* Audio Element */}
                <audio
                    ref={audioRef}
                    src={activeSrc || undefined}
                    controls
                    onLoadedMetadata={() => {
                        handleMetadata();
                        // If we just applied a resume seek
                        if (seekApplied && resumeCandidate && currentIndex === resumeCandidate.trackIndex) {
                            if (audioRef.current) audioRef.current.currentTime = resumeCandidate.position;
                        }
                    }}
                    onCanPlay={async () => {
                        console.log(`[AudioPlayerOverlay] onCanPlay event fired. pendingPlayRequested: ${pendingPlayRequestedRef.current}, activeSrc: ${activeSrc}`);
                        if (pendingPlayRequestedRef.current && audioRef.current) {
                            console.log("[AudioPlayerOverlay] pendingPlayRequested is true. Triggering audioRef.current.play()");
                            pendingPlayRequestedRef.current = false;
                            try {
                                await audioRef.current.play();
                                console.log("[AudioPlayerOverlay] play() resolved successfully");
                            } catch (e: any) {
                                console.error("[AudioPlayerOverlay] play() rejected:", e);
                                setError(`Autoplay blocked: ${e.message || e}. Tap Play to start.`);
                            }
                        }
                    }}
                    onEnded={() => {
                        handleEnded();
                        // Save "finished" state or next track start
                        if (bookId) {
                            LibraryDB.saveAudioResume({
                                bookId,
                                trackIndex: currentIndex + 1, // Next track
                                positionSeconds: 0,
                                updatedAt: Date.now()
                            });
                        }
                    }}
                    onError={handleError}
                    onTimeUpdate={() => {
                        if (!audioRef.current) return;

                        // Sleep Timer Logic
                        if (sleepTimerEndAt && Date.now() >= sleepTimerEndAt) {
                            audioRef.current.pause();
                            setSleepTimerEndAt(null);
                            localStorage.removeItem('audio_sleep_timer_endAt');
                            setSleepToast(true);
                            setTimeout(() => setSleepToast(false), 4000);
                            return;
                        }

                        // 1. Upsell Logic
                        if (!isMember && (currentIndex > 0 || audioRef.current.currentTime > 300)) {
                            audioRef.current.pause();
                            setShowUpsell(true);
                            return;
                        }

                        // 2. Resume Save Logic (Throttle ~5s)
                        const now = Date.now();
                        if (bookId && now - lastTickRef.current > 5000) {
                            lastTickRef.current = now;
                            LibraryDB.saveAudioResume({
                                bookId,
                                trackIndex: currentIndex,
                                positionSeconds: audioRef.current.currentTime,
                                updatedAt: now
                            });
                        }
                    }}
                    onPause={() => {
                        if (bookId && audioRef.current) {
                            LibraryDB.saveAudioResume({
                                bookId,
                                trackIndex: currentIndex,
                                positionSeconds: audioRef.current.currentTime,
                                updatedAt: Date.now()
                            });
                        }
                    }}
                    controlsList="nodownload"
                    style={{
                        width: '100%',
                        borderRadius: '30px',
                        outline: 'none',
                        display: (!loading && !error && activeSrc) ? 'block' : 'none'
                    }}
                />

                {/* Upsell Modal */}
                {showUpsell && (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 5000,
                        background: 'rgba(0,0,0,0.95)',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                        color: 'white', padding: '20px', textAlign: 'center'
                    }} onClick={e => e.stopPropagation()}>
                        <h2 style={{ fontSize: '2rem', marginBottom: '20px' }}>⏳ Preview Ended</h2>
                        <p style={{ maxWidth: '400px', fontSize: '1.2rem', marginBottom: '30px', lineHeight: 1.5 }}>
                            You’ve enjoyed the 5-minute preview. <br />
                            <b>Join the Book Club</b> to unlock the full narrated audiobook and support the project.
                        </p>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px', width: '100%', maxWidth: '300px' }}>
                            <button onClick={() => alert("Membership flow placeholder")} style={{
                                padding: '15px', borderRadius: '30px', border: 'none',
                                background: 'var(--accent-color)', color: 'white',
                                fontSize: '1.1rem', fontWeight: 'bold', cursor: 'pointer'
                            }}>
                                Join Book Club
                            </button>

                            <button onClick={onClose} style={{
                                padding: '15px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.3)',
                                background: 'transparent', color: 'white',
                                fontSize: '1rem', cursor: 'pointer'
                            }}>
                                Keep Reading (Text)
                            </button>

                            {/* Optional Upsell Audio */}
                            {upsellUrl && (
                                <div style={{ marginTop: '10px' }}>
                                    <button onClick={() => {
                                        if (upsellRef.current) {
                                            if (upsellPlaying) upsellRef.current.pause();
                                            else upsellRef.current.play();
                                            setUpsellPlaying(!upsellPlaying);
                                        }
                                    }} style={{ background: 'none', border: 'none', color: '#aaa', textDecoration: 'underline', cursor: 'pointer' }}>
                                        {upsellPlaying ? 'Stop Message' : '▶ Play Message'}
                                    </button>
                                    <audio ref={upsellRef} src={upsellUrl || undefined} onEnded={() => setUpsellPlaying(false)} />
                                </div>
                            )}
                        </div>

                        <div style={{ marginTop: '40px', fontSize: '0.9rem', opacity: 0.6 }}>
                            Looking for free TTS? <span onClick={() => { onClose(); alert("Use the standard Play button on any page for free device TTS."); }} style={{ textDecoration: 'underline', cursor: 'pointer' }}>Use on-device voice.</span>
                        </div>
                    </div>
                )}

                {!loading && !error && !activeSrc && (
                    <div style={{ padding: '20px', opacity: 0.5, color: 'white' }}>No audio available</div>
                )}

                {/* Debug Button */}
                {error && (
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            const debugInfo = {
                                title,
                                url,
                                trackIndex: currentIndex,
                                trackUrl: activeSrc,
                                error,
                                timestamp: new Date().toISOString(),
                                userAgent: navigator.userAgent
                            };
                            navigator.clipboard.writeText(JSON.stringify(debugInfo, null, 2));
                            alert("Copied debug info to clipboard");
                        }}
                        style={{
                            fontSize: '0.8rem', opacity: 0.7, background: 'transparent', border: 'none', color: '#ffaaaa', cursor: 'pointer', textDecoration: 'underline'
                        }}
                    >
                        Copy Debug Details
                    </button>
                )}

                <div style={{ display: 'flex', gap: '10px', width: '100%' }}>
                    <button
                        onClick={onClose}
                        style={{
                            flex: 1,
                            padding: '10px', background: 'transparent',
                            border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                            cursor: 'pointer', color: 'white'
                        }}
                    >
                        Close Player
                    </button>
                    {/* Sleep Timer Button */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => setShowSleepPicker(!showSleepPicker)}
                            style={{
                                width: sleepTimerEndAt ? 'auto' : '44px',
                                minWidth: '44px',
                                padding: '10px', background: sleepTimerEndAt ? 'rgba(255,255,255,0.1)' : 'transparent',
                                border: '1px solid rgba(255,255,255,0.2)', borderRadius: '8px',
                                cursor: 'pointer', color: 'white', fontSize: '1.2rem',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px'
                            }}
                            title={sleepTimerEndAt ? `Sleep timer: ${Math.max(0, Math.ceil((sleepTimerEndAt - Date.now()) / 60000))} minutes remaining` : "Sleep timer"}
                            aria-label={sleepTimerEndAt ? `Sleep timer: ${Math.max(0, Math.ceil((sleepTimerEndAt - Date.now()) / 60000))} minutes remaining` : "Sleep timer"}
                        >
                            🌙
                            {sleepTimerEndAt && (
                                <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>
                                    {Math.max(0, Math.ceil((sleepTimerEndAt - Date.now()) / 60000))}m
                                </span>
                            )}
                        </button>

                        {showSleepPicker && (
                            <div style={{
                                position: 'absolute', bottom: '100%', right: 0, marginBottom: '10px',
                                background: 'rgba(30,30,30,0.95)', border: '1px solid rgba(255,255,255,0.2)',
                                borderRadius: '12px', padding: '8px', display: 'flex', flexDirection: 'column',
                                minWidth: '120px', zIndex: 4000, boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
                                backdropFilter: 'blur(10px)'
                            }}>
                                {[
                                    { label: 'Off', val: 0 },
                                    { label: '5 min', val: 5 },
                                    { label: '15 min', val: 15 },
                                    { label: '30 min', val: 30 },
                                    { label: '45 min', val: 45 },
                                    { label: '1 hr', val: 60 },
                                    { label: '2 hr', val: 120 }
                                ].map(opt => (
                                    <button
                                        key={opt.label}
                                        onClick={() => {
                                            if (opt.val === 0) {
                                                setSleepTimerEndAt(null);
                                                localStorage.removeItem('audio_sleep_timer_endAt');
                                            } else {
                                                const endAt = Date.now() + opt.val * 60000;
                                                setSleepTimerEndAt(endAt);
                                                localStorage.setItem('audio_sleep_timer_endAt', endAt.toString());
                                            }
                                            setShowSleepPicker(false);
                                        }}
                                        style={{
                                            padding: '12px 16px', background: 'transparent', border: 'none',
                                            color: 'white', cursor: 'pointer', textAlign: 'left',
                                            fontSize: '1rem', borderRadius: '6px',
                                            transition: 'background 0.2s',
                                            outline: 'none'
                                        }}
                                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
                                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Resume Prompt Modal */}
            {showResumePrompt && resumeCandidate && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 6000,
                    background: 'rgba(0,0,0,0.85)',
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    color: 'white', padding: '20px'
                }} onClick={(e) => e.stopPropagation()}>
                    <h3 style={{ marginBottom: '20px', fontSize: '1.5rem' }}>Resume Playback?</h3>
                    <p style={{ marginBottom: '30px', opacity: 0.8 }}>
                        Pick up where you left off at {new Date(resumeCandidate.position * 1000).toISOString().substr(14, 5)} (Part {resumeCandidate.trackIndex + 1})?
                    </p>
                    <div style={{ display: 'flex', gap: '20px' }}>
                        <button onClick={() => {
                            // RESUME
                            setSeekApplied(true); // Will trigger seek in loadedmetadata
                            setShowResumePrompt(false);
                            playTrack(resumeCandidate.trackIndex, true);
                            if (audioRef.current) {
                                audioRef.current.play().catch(e => console.log("[AudioPlayerOverlay] Inline resume play blocked:", e));
                            }
                        }} style={{
                            padding: '15px 30px', borderRadius: '30px', border: 'none',
                            background: 'var(--accent-color)', color: 'white', fontWeight: 'bold', fontSize: '1.1rem', cursor: 'pointer'
                        }}>
                            Resume
                        </button>
                        <button onClick={() => {
                            // START OVER
                            if (bookId) LibraryDB.deleteAudioResume(bookId);
                            setResumeCandidate(null);
                            setShowResumePrompt(false);
                            playTrack(0, true);
                            if (audioRef.current) {
                                audioRef.current.play().catch(e => console.log("[AudioPlayerOverlay] Inline start over play blocked:", e));
                            }
                        }} style={{
                            padding: '15px 30px', borderRadius: '30px', border: '1px solid rgba(255,255,255,0.3)',
                            background: 'transparent', color: 'white', fontSize: '1.1rem', cursor: 'pointer'
                        }}>
                            Start Over
                        </button>
                    </div>
                </div>
            )}

            {/* Sleep Timer Toast */}
            {sleepToast && (
                <div style={{
                    position: 'absolute', bottom: '100px', left: '50%', transform: 'translateX(-50%)',
                    background: 'rgba(0,0,0,0.85)', color: 'white', padding: '12px 24px',
                    borderRadius: '30px', zIndex: 5000, animation: 'fadeIn 0.3s',
                    border: '1px solid rgba(255,255,255,0.3)', boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                    fontWeight: 'bold', minWidth: 'max-content'
                }}>
                    Sleep timer ended — playback paused.
                </div>
            )}

            <style>{`
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            `}</style>
        </div>
    );
};
